import { Component, Input, Output, EventEmitter } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ChangeEvent } from '../../domain/regs-models';
import { QuotaBarComponent } from '../widgets/quota-bar.component';

@Component({
  selector: 'app-keep-it-popout',
  standalone: true,
  imports: [CommonModule, QuotaBarComponent],
  template: `
    <div class="popout-container" *ngIf="event">
      <div class="popout-card">
        <!-- Top header row -->
        <div class="popout-header">
          <span class="following-tag">FOLLOWING · {{ event.short.toUpperCase() }}</span>
          <button type="button" class="btn-dismiss" (click)="dismiss.emit()" title="Dismiss">
            <span class="material-icons">close</span>
          </button>
        </div>

        <!-- Headline & Text -->
        <div class="popout-title">{{ event.title }}</div>
        <div class="popout-text">{{ event.text }}</div>

        <!-- Live Quota Progress Bar (if quota alert) -->
        <div *ngIf="hasQuotaData" class="quota-preview">
          <app-quota-bar
            [pct]="quotaPct!"
            [prevPct]="prevPct"
            [trackColor]="'#DCE6F5'"
            [height]="10"
          ></app-quota-bar>
          <div class="quota-preview-sub">
            <span>{{ formatPct(quotaPct!) }}% landed</span>
            <span *ngIf="prevPct != null"> · was {{ formatPct(prevPct) }}%</span>
            <span class="quota-alert-tag">Alert at 80%</span>
          </div>
        </div>

        <!-- Action Buttons -->
        <div class="popout-actions">
          <button type="button" class="btn-details" (click)="details.emit()">
            See details
          </button>
          <button type="button" class="btn-got-it" (click)="dismiss.emit()">
            Got it
          </button>
        </div>
      </div>
    </div>
  `,
  styles: [`
    .popout-container {
      position: fixed;
      bottom: 96px;
      left: 16px;
      right: 16px;
      max-width: 440px;
      margin: 0 auto;
      z-index: 9990;
      animation: popIn 0.25s cubic-bezier(0.16, 1, 0.3, 1);
    }

    @keyframes popIn {
      from {
        opacity: 0;
        transform: translateY(20px);
      }
      to {
        opacity: 1;
        transform: translateY(0);
      }
    }

    .popout-card {
      background: #ffffff;
      border: 1px solid #DCE6F5;
      border-radius: 20px;
      padding: 14px 16px;
      box-shadow: 0 10px 28px rgba(15, 28, 46, 0.22);
      display: flex;
      flex-direction: column;
    }

    .popout-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 4px;
    }

    .following-tag {
      font-size: 11px;
      font-weight: 700;
      letter-spacing: 0.6px;
      color: #007AFF;
    }

    .btn-dismiss {
      width: 28px;
      height: 28px;
      border-radius: 50%;
      background: #F0F4F8;
      border: none;
      display: flex;
      align-items: center;
      justify-content: center;
      color: #5A6E85;
      cursor: pointer;
    }

    .btn-dismiss:hover {
      background: #E2E8F0;
    }

    .btn-dismiss .material-icons {
      font-size: 16px;
    }

    .popout-title {
      font-size: 16px;
      font-weight: 700;
      color: #0F1C2E;
      margin-bottom: 4px;
    }

    .popout-text {
      font-size: 13.5px;
      line-height: 1.35;
      color: #2C2C2E;
    }

    .quota-preview {
      margin-top: 10px;
    }

    .quota-preview-sub {
      display: flex;
      justify-content: space-between;
      font-size: 12px;
      color: #5A6E85;
      margin-top: 5px;
    }

    .quota-alert-tag {
      color: #8C9CAE;
    }

    .popout-actions {
      display: flex;
      gap: 8px;
      margin-top: 12px;
    }

    .btn-details {
      flex: 1;
      height: 36px;
      border-radius: 12px;
      background: #007AFF;
      color: #ffffff;
      border: none;
      font-size: 14px;
      font-weight: 600;
      cursor: pointer;
      transition: background 0.15s ease;
    }

    .btn-details:hover {
      background: #0066D6;
    }

    .btn-got-it {
      flex: 1;
      height: 36px;
      border-radius: 12px;
      background: #ffffff;
      color: #0F1C2E;
      border: 1px solid #D1D9E6;
      font-size: 14px;
      font-weight: 600;
      cursor: pointer;
      transition: background 0.15s ease;
    }

    .btn-got-it:hover {
      background: #F8FAFD;
    }
  `],
})
export class KeepItPopoutComponent {
  @Input() event!: ChangeEvent;
  @Output() details = new EventEmitter<void>();
  @Output() dismiss = new EventEmitter<void>();

  get firstRow(): Record<string, any> | null {
    return this.event.rows && this.event.rows.length > 0 ? this.event.rows[0] : null;
  }

  get hasQuotaData(): boolean {
    const r = this.firstRow;
    return !!(r && typeof r['pct'] === 'number' && typeof r['landed'] === 'number');
  }

  get quotaPct(): number | null {
    const r = this.firstRow;
    return r && typeof r['pct'] === 'number' ? r['pct'] : null;
  }

  get prevPct(): number | null {
    const r = this.firstRow;
    const now = this.quotaPct;
    const prev = r && typeof r['prev_pct'] === 'number' ? r['prev_pct'] : null;
    return prev != null && prev !== now ? prev : null;
  }

  formatPct(v: number): string {
    const r = Math.round(v * 10) / 10;
    return r === Math.round(r) ? r.toFixed(0) : r.toString();
  }
}
